import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { setMpin } from "../../services/authServices";
import logger from "../../utils/logger";

const PostLoginMpinSetup: React.FC = () => {
  const [mpin, setMpinValue] = useState<string>("");
  const [confirmMpin, setConfirmMpin] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showMpin, setShowMpin] = useState<boolean>(false);
  const [showConfirmMpin, setShowConfirmMpin] = useState<boolean>(false);
  const navigate = useNavigate();

  // Check if this is a reset or new setup
  const isReset = localStorage.getItem("mpinSetupType") === "reset";

  // Verify user is authenticated and setup flags are set
  useEffect(() => {
    const authToken = localStorage.getItem("auth_token");
    const showMpinSetup = localStorage.getItem("showMpinSetup");
    const mpinSetupType = localStorage.getItem("mpinSetupType");

    logger.log("[MPIN Setup] Component mounted with state:", {
      hasAuthToken: !!authToken,
      showMpinSetup,
      mpinSetupType,
    });

    // If user is not authenticated or flags aren't set, redirect to login
    if (!authToken || showMpinSetup !== "true" || !mpinSetupType) {
      logger.warn("[MPIN Setup] Missing required state, redirecting to login");
      toast.error("Invalid setup state. Please try again.");
      navigate("/", { replace: true });
    }
  }, [navigate]);

  const handleMpinChange = (value: string, field: 'mpin' | 'confirm') => {
    // Only allow numeric input and limit to 4 digits
    const numericValue = value.replace(/\D/g, '').slice(0, 4);
    
    if (field === 'mpin') {
      setMpinValue(numericValue);
    } else {
      setConfirmMpin(numericValue);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (mpin.length !== 4) {
      toast.error("MPIN must be 4 digits");
      return;
    }

    if (mpin !== confirmMpin) {
      toast.error("MPIN and confirmation do not match");
      return;
    }

    setIsLoading(true);

    try {
      // Don't send oldMpin key at all when setting for first time
      await setMpin({ mpin });

      logger.log("[MPIN Setup] PIN successfully set:", {
        setupType: isReset ? 'reset' : 'setup',
        timestamp: new Date().toISOString()
      });

      toast.success(`PIN ${isReset ? 'reset' : 'set'} successfully! You can now use it for quick login.`);

      // Clear the flags and navigate to main app
      localStorage.removeItem("showMpinSetup");
      localStorage.removeItem("mpinSetupType");
      logger.log("[MPIN Setup] Flags cleared, navigating to leads");

      navigate("/leads", { replace: true });
    } catch (error: any) {
      logger.error("[MPIN Setup] Error setting MPIN:", error);
      
      // Extract specific error message from API response
      const errorMessage = error.response?.data?.message || "Failed to set MPIN. Please try again.";
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="container">
      <div className="row justify-content-center login_section">
        <div className="col-12 col-md-6 col-lg-5 col-xl-4 login_card">
          <div
            className="card login-card shadow-lg p-4"
            style={{
              maxWidth: "650px",
              width: "100%",
              margin: "0 auto",
              borderRadius: "15px",
              backgroundColor: "#ebeff4",
            }}
          >
            <div className="d-flex flex-column align-items-center" style={{ width: "100%" }}>
              <img
                src="assets/img/3sigma_logo.png"
                alt="logo"
                className="mb-4"
                style={{
                  width: "100%",
                  maxWidth: "200px",
                  height: "auto",
                  objectFit: "contain",
                }}
              />
              <div className="login-header text-center mb-4">
                <h2 style={{ fontWeight: "bold", color: "#333", fontSize: "1.75rem" }}>
                  {isReset ? "Reset Your PIN" : "Set Your PIN"}
                </h2>
                <p className="text-muted mt-2" style={{ fontSize: "0.95rem" }}>
                  {isReset ? "Create a new 4-digit PIN" : "Create a 4-digit PIN for quick login"}
                </p>
              </div>
              
              <form onSubmit={handleSubmit} className="login_form w-100">
                <div className="form_container_login">
                  <div className="mb-3">
                    <label className="form-label" style={{ fontWeight: "600", color: "#333" }}>
                      Enter 4-digit PIN
                    </label>
                    <div className="position-relative">
                      <input
                        type={showMpin ? "text" : "password"}
                        className="form-control"
                        value={mpin}
                        onChange={(e) => handleMpinChange(e.target.value, 'mpin')}
                        placeholder="••••"
                        maxLength={4}
                        style={{
                          height: "50px",
                          fontSize: "20px",
                          textAlign: "center",
                          letterSpacing: "12px",
                          borderRadius: "8px",
                          border: "2px solid #ddd",
                          fontWeight: "bold",
                          paddingRight: "45px",
                        }}
                      />
                      <button
                        type="button"
                        className="btn btn-link position-absolute"
                        onClick={() => setShowMpin(!showMpin)}
                        style={{
                          right: "10px",
                          top: "50%",
                          transform: "translateY(-50%)",
                          padding: "0",
                          border: "none",
                          background: "none",
                          fontSize: "18px",
                          color: "#6c757d",
                        }}
                      >
                        {showMpin ? "🙈" : "👁️"}
                      </button>
                    </div>
                  </div>
                  
                  <div className="mb-3">
                    <label className="form-label" style={{ fontWeight: "600", color: "#333" }}>
                      Confirm PIN
                    </label>
                    <div className="position-relative">
                      <input
                        type={showConfirmMpin ? "text" : "password"}
                        className="form-control"
                        value={confirmMpin}
                        onChange={(e) => handleMpinChange(e.target.value, 'confirm')}
                        placeholder="••••"
                        maxLength={4}
                        style={{
                          height: "50px",
                          fontSize: "20px",
                          textAlign: "center",
                          letterSpacing: "12px",
                          borderRadius: "8px",
                          border: "2px solid #ddd",
                          fontWeight: "bold",
                          paddingRight: "45px",
                        }}
                      />
                      <button
                        type="button"
                        className="btn btn-link position-absolute"
                        onClick={() => setShowConfirmMpin(!showConfirmMpin)}
                        style={{
                          right: "10px",
                          top: "50%",
                          transform: "translateY(-50%)",
                          padding: "0",
                          border: "none",
                          background: "none",
                          fontSize: "18px",
                          color: "#6c757d",
                        }}
                      >
                        {showConfirmMpin ? "🙈" : "👁️"}
                      </button>
                    </div>
                  </div>
                  
                  <button
                    type="submit"
                    className="btn btn-primary w-100 mt-3"
                    style={{
                      backgroundColor: "#3A4B86",
                      fontWeight: "bold",
                      fontSize: "1.1rem",
                      borderRadius: "8px",
                      height: "50px",
                    }}
                    disabled={isLoading || mpin.length !== 4 || confirmMpin.length !== 4}
                  >
                    {isLoading 
                      ? `${isReset ? 'Resetting' : 'Setting'} PIN...` 
                      : `${isReset ? 'Reset' : 'Set'} PIN`
                    }
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PostLoginMpinSetup;